// The portal frame: the navy sidebar (logo, search, role-aware nav: Overview, Invoice Queue, Reports, Audit log, Settings; tenant card), the scrolling main area,
// and the always-on banner when staff open a customer.
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useSession } from "../auth/SessionProvider";
import { useHub } from "../hub/useHub";
import { setLivePolling, useNeedsYou, useSettingsAttention } from "../api/hooks";
import { Icon } from "../ui";
import { ToastProvider, useToast } from "../ui/toast";
import { WELCOME_KEY } from "../screens/Invite";
import { takeNotice } from "../shared/notice";
import { roleWord } from "../shared/format";

function NavItem({ to, icon, label, count, end, dot }: { to: string; icon: string; label: string; count?: number; end?: boolean; dot?: boolean }) {
  return (
    <NavLink to={to} end={end} className="avp-nav-link" style={({ isActive }) => ({ position: "relative", display: "flex", alignItems: "center", gap: 12, height: 44, padding: "0 14px", borderRadius: "var(--radius-md)", background: isActive ? "rgba(0,131,192,.34)" : "transparent", color: isActive ? "#fff" : "var(--av-sky)", font: "700 15px var(--font-body)", transition: "background var(--dur-fast) var(--ease-out)" })}>
      {({ isActive }) => (
        <>
          <Icon name={icon} size={19} style={{ color: isActive ? "var(--av-cyan-400)" : "inherit" }} />
          <span className="avp-nav-label" style={{ flex: 1 }}>{label}</span>
          {count ? <span style={{ minWidth: 22, height: 22, padding: "0 7px", borderRadius: 99, background: "var(--av-sun-400)", color: "var(--av-navy-900)", font: "800 11px/22px var(--font-body)", textAlign: "center" }}>{count}</span> : null}
          {!count && dot ? <span aria-label="Needs attention" style={{ width: 8, height: 8, borderRadius: 99, background: "var(--av-sun-400)", flex: "none" }} /> : null}
        </>
      )}
    </NavLink>
  );
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "AV";
}

/** Straight after an invitation is accepted: a welcome to the workspace, once. */
function WelcomeToast({ ready }: { ready: boolean }) {
  const toast = useToast();
  useEffect(() => {
    if (!ready) return;
    const notice = takeNotice();
    if (notice) toast({ tone: "success", title: notice });
    const company = sessionStorage.getItem(WELCOME_KEY);
    if (!company) return;
    sessionStorage.removeItem(WELCOME_KEY);
    toast({ tone: "success", title: `Welcome to ${company}` });
  }, [ready, toast]);
  return null;
}

/** Settings in the sidebar carries the amber dot when any section needs attention. */
function SettingsNavItem() {
  const attention = useSettingsAttention();
  return <NavItem to="/settings" icon="settings-2" label="Settings" dot={Object.values(attention).some(Boolean)} />;
}

export function Shell({ children }: { children?: ReactNode }) {
  const auth = useAuth();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);
  // No live connection while a person in several workspaces is still choosing one: there is nothing to join yet.
  const hub = useHub(session.tenantId, session.isStaff || !!session.tenantId);
  useEffect(() => setLivePolling(hub !== "connected"), [hub]);
  const needs = useNeedsYou();
  const needCount = needs.data?.items.filter((i) => !i.resolved).length ?? 0;
  const tenant = session.tenant;

  useEffect(() => setMenu(false), [location.pathname]);

  const search = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const po = /^(\d{4,7})-(\d{1,3})$/.exec(t);
    if (po) { navigate(`/po/${po[1]}/${po[2]}`); setQ(""); return; }
    navigate(`/reports/invoices?q=${encodeURIComponent(t)}`);
    setQ("");
  };

  return (
    <ToastProvider offsetLeft={248}>
      <WelcomeToast ready={!!tenant} />
      <div style={{ display: "flex", height: "100vh" }}>
        <aside className="avp-sidebar" style={{ flex: "none", background: "var(--surface-inverse)", color: "#fff", display: "flex", flexDirection: "column", padding: "22px 14px 16px", gap: 4, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "var(--gradient-glow)", opacity: 0.6, pointerEvents: "none" }} />
          <img src="/logo-wordmark-inverse.png" alt="AutoVouch" className="avp-side-text" style={{ height: 40, objectFit: "contain", objectPosition: "left", margin: "0 6px 20px", position: "relative", alignSelf: "flex-start" }} />
          {tenant && (
            <form className="avp-side-search" onSubmit={(e) => { e.preventDefault(); search(q); }} style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 14px", marginBottom: 14, borderRadius: 99, background: "rgba(255,255,255,.08)", boxShadow: "inset 0 0 0 1px var(--border-inverse)" }}>
              <Icon name="search" size={16} style={{ color: "var(--av-sky)" }} />
              <input className="avp-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }} placeholder="Invoice #, PO or supplier" aria-label="Search invoices" style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: "transparent", color: "#fff", font: "14px var(--font-body)" }} />
            </form>
          )}
          {tenant ? (
            <>
              <NavItem to="/" end icon="layout-dashboard" label="Overview" />
              <NavItem to="/queue" icon="inbox" label="Invoice Queue" count={needCount} />
              <NavItem to="/reports" icon="chart-column" label="Reports" />
              <div style={{ height: 1, background: "var(--border-inverse)", margin: "10px 6px", position: "relative" }} />
              <NavItem to="/audit" icon="list-checks" label="Audit log" />
              <SettingsNavItem />
            </>
          ) : null}
          <div style={{ flex: 1 }} />
          {session.isStaff && (
            <>
              <NavItem to="/ops" icon="radar" label="Mission control" />
              <NavItem to="/staff" icon="shield" label="Staff console" />
            </>
          )}
          <div style={{ position: "relative", marginTop: 8 }}>
            {menu && (
              <div style={{ position: "absolute", left: 0, right: 0, bottom: "calc(100% + 8px)", padding: 10, borderRadius: "var(--radius-lg)", background: "var(--av-navy-700)", boxShadow: "var(--shadow-lg)", display: "flex", flexDirection: "column", gap: 6, zIndex: 20 }}>
                <div style={{ padding: "4px 8px 8px", font: "12px var(--font-body)", color: "var(--av-sky)", wordBreak: "break-all" }}>{auth.account?.email ?? auth.account?.name}</div>
                {session.isStaff && tenant && (
                  <button type="button" onClick={() => { session.openTenant(null); navigate("/staff"); }} style={{ display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", borderRadius: 10, border: 0, background: "rgba(255,255,255,.08)", color: "#fff", font: "700 13px var(--font-body)", cursor: "pointer" }}><Icon name="repeat" size={15} />Leave {tenant.name}</button>
                )}
                {!session.isStaff && session.workspaces.length > 1 && (
                  <button type="button" onClick={() => { session.openTenant(null); navigate("/"); }} style={{ display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", borderRadius: 10, border: 0, background: "rgba(255,255,255,.08)", color: "#fff", font: "700 13px var(--font-body)", cursor: "pointer" }}><Icon name="repeat" size={15} />Switch workspace</button>
                )}
                <button type="button" onClick={auth.signOut} style={{ display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", borderRadius: 10, border: 0, background: "rgba(255,255,255,.08)", color: "#fff", font: "700 13px var(--font-body)", cursor: "pointer" }}><Icon name="log-out" size={15} />Sign out</button>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
            <button type="button" onClick={() => setMenu(!menu)} aria-haspopup="menu" aria-expanded={menu} style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: "var(--radius-lg)", background: "rgba(255,255,255,.06)", boxShadow: "inset 0 0 0 1px var(--border-inverse)", cursor: "pointer", border: 0, color: "#fff", textAlign: "left" }}>
              <span style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--gradient-fresh)", display: "flex", alignItems: "center", justifyContent: "center", font: "800 13px var(--font-display)", flex: "none" }}>{initials(tenant?.name ?? auth.account?.name ?? "AutoVouch")}</span>
              <span className="avp-side-text" style={{ minWidth: 0 }}>
                <span style={{ display: "block", font: "800 14px var(--font-display)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tenant?.name ?? "AutoVouch"}</span>
                <span style={{ display: "block", font: "12px var(--font-body)", color: "var(--av-sky)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.me.user.name ?? auth.account?.email ?? ""} – {roleWord(session.role)}</span>
              </span>
            </button>
            </div>
          </div>
        </aside>
        <main id="avp-main" style={{ flex: 1, minWidth: 0, overflow: "auto", background: "linear-gradient(180deg,#eaf6fc 0,#f5f9fc 360px)" }}>
          {session.openedAs && tenant && (
            <div style={{ position: "sticky", top: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 12, padding: "10px 24px", background: "var(--av-sun-400)", color: "var(--av-navy-900)", font: "700 14px var(--font-body)" }}>
              <Icon name="shield" size={16} />Viewing {tenant.name} as staff – every action is logged as {session.me.user.name ?? "AutoVouch staff"}
              <span style={{ flex: 1 }} />
              <button type="button" onClick={() => { session.openTenant(null); navigate("/staff"); }} style={{ border: 0, background: "var(--av-navy-900)", color: "#fff", borderRadius: 99, height: 30, padding: "0 14px", font: "700 13px var(--font-body)", cursor: "pointer" }}>Exit</button>
            </div>
          )}
          {hub === "disconnected" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 24px", background: "var(--status-review-bg)", color: "var(--status-review-fg)", font: "600 13px var(--font-body)" }}><Icon name="cloud-off" size={15} />Live updates are off – refreshing every few seconds instead</div>
          )}
          {children ?? <Outlet />}
        </main>
      </div>
    </ToastProvider>
  );
}

export function Page({ width = 1200, children }: { width?: number; children: ReactNode }) {
  return <div className="avp-main-pad" style={{ maxWidth: width, margin: "0 auto" }}>{children}</div>;
}
